import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserRole } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY as NO_VALUE } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';
import type { AuthUser } from '@/auth/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/select';
import { SkeletonLines } from '@/components/ui/skeleton';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/components/ui/toaster';
import { SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';
import { useSettingsQuery } from '@/pages/settings/useSettings';

const PASSWORD_MIN = 10;

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z.string().min(PASSWORD_MIN, `At least ${PASSWORD_MIN} characters`),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match',
  });
type PasswordValues = z.infer<typeof schema>;

const EMPTY: PasswordValues = { currentPassword: '', newPassword: '', confirmPassword: '' };

/** One row of `GET /users/directory` (the same list the position owner pickers use). */
interface DirectoryUser {
  id: string;
  name: string;
  title: string | null;
  email: string;
  active: boolean;
}

/** Dropdown value for "sign as myself" (`documentSignerId: null`). */
const ME = '__me__';
/** The mailbox printed with the name hidden, until the settings say otherwise. */
const DEFAULT_GENERIC_EMAIL = 'hello@data-claw.de';

const documentsSchema = z.object({ signer: z.string(), hideName: z.boolean() });
type DocumentsValues = z.infer<typeof documentsSchema>;

function toDocumentsValues(user: AuthUser): DocumentsValues {
  return { signer: user.documentSignerId ?? ME, hideName: user.documentHideName ?? false };
}

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Who appears as the contact on the documents this user generates (decision 58): another active
 * user, or nobody (the company name and its shared mailbox). Saved with `PATCH /auth/me`.
 */
function DocumentsCard({ user, reload }: { user: AuthUser; reload: () => Promise<void> }) {
  const signers = useQuery({
    queryKey: queryKeys.list('users', { scope: 'directory' }),
    queryFn: () => api.get<DirectoryUser[]>('/users/directory'),
    staleTime: 5 * 60_000,
  });
  const settings = useSettingsQuery();
  const genericEmail = settings.data?.company.genericEmail || DEFAULT_GENERIC_EMAIL;

  const values = React.useMemo(() => toDocumentsValues(user), [user]);
  const form = useForm<DocumentsValues>({
    resolver: zodResolver(documentsSchema),
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
  });
  const hideName = form.watch('hideName');

  const options = React.useMemo(() => {
    const others = (signers.data ?? []).filter((s) => s.active && s.id !== user.id).sort(byName);
    return [
      { value: ME, label: `Me (${user.name})` },
      ...others.map((s) => ({ value: s.id, label: s.title ? `${s.name} (${s.title})` : s.name })),
    ];
  }, [signers.data, user.id, user.name]);

  const save = useMutation({
    mutationFn: (v: DocumentsValues) =>
      api.patch<AuthUser>('/auth/me', {
        documentSignerId: v.signer === ME ? null : v.signer,
        documentHideName: v.hideName,
      }),
    onSuccess: async (saved) => {
      toast.success('Document settings saved');
      form.reset(toDocumentsValues(saved), { keepDirtyValues: false });
      await reload();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <SettingsCard
      title="Documents"
      description="Who is named as the contact on the Job Descriptions and Candidate Profiles you generate, in the closing box and in the apply sentence."
    >
      {signers.isLoading ? (
        <SkeletonLines lines={2} />
      ) : signers.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Users could not be loaded: {signers.error.message}
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="max-w-sm space-y-4" noValidate>
            <FormField
              control={form.control}
              name="signer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact person on generated documents</FormLabel>
                  <FormControl>
                    <SimpleSelect
                      aria-label="Contact person on generated documents"
                      value={field.value}
                      onChange={(v) => field.onChange(v ?? ME)}
                      options={options}
                      disabled={hideName}
                    />
                  </FormControl>
                  <FormDescription>
                    Their name, title and e-mail replace yours on new documents.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="hideName"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                    </FormControl>
                    <FormLabel className="font-normal">Hide my name – use {genericEmail}</FormLabel>
                  </div>
                  <FormDescription>
                    Documents then show only the company name and {genericEmail}, nobody&apos;s name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" loading={save.isPending} disabled={!form.formState.isDirty}>
              Save
            </Button>
          </form>
        </Form>
      )}
    </SettingsCard>
  );
}

/**
 * `/settings/account` (every role): who is signed in and the change-password form. The API revokes
 * every web session of the user after a password change, so the page signs out and sends the user
 * back to the login screen.
 */
export function AccountSettings() {
  const { user, logout, reload } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<PasswordValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });
  const { setError: setFieldError } = form;

  const change = useMutation({
    mutationFn: (v: PasswordValues) =>
      api.post('/auth/change-password', { currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: async () => {
      toast.success('Password changed. Please sign in again.');
      await logout();
      navigate('/login', { replace: true });
    },
    onError: (err: Error) => {
      if (isApiError(err)) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setFieldError('currentPassword', { message: 'The current password is wrong' });
          return;
        }
        const fields = err.fieldErrors();
        if (fields.newPassword) setFieldError('newPassword', { message: fields.newPassword });
        if (fields.currentPassword) setFieldError('currentPassword', { message: fields.currentPassword });
        if (err.code === 'WEAK_PASSWORD') {
          setFieldError('newPassword', { message: err.message });
          return;
        }
        if (Object.keys(fields).length > 0) return;
      }
      setError(err.message);
    },
  });

  if (!user) return null;

  return (
    <SettingsSection title="Account" description="Your sign-in details.">
      <SettingsCard
        title="Signed in as"
        description="Your name, title and e-mail are the contact on the documents you generate, unless you choose otherwise under Documents below. An administrator sets the title under Users & roles."
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Name</dt>
          <dd>{user.name}</dd>
          <dt className="text-muted-foreground">Title</dt>
          <dd className={user.title ? undefined : 'text-muted-foreground'}>{user.title || NO_VALUE}</dd>
          <dt className="text-muted-foreground">E-mail</dt>
          <dd>{user.email}</dd>
          <dt className="text-muted-foreground">Role</dt>
          <dd>{UserRole.label(user.role)}</dd>
        </dl>
      </SettingsCard>

      <DocumentsCard user={user} reload={reload} />

      <SettingsCard
        title="Change password"
        description="After the change every session of yours is signed out, including this one."
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((v) => {
              setError(null);
              change.mutate(v);
            })}
            className="max-w-sm space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormDescription>At least {PASSWORD_MIN} characters.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <Button type="submit" loading={change.isPending}>
              Change password
            </Button>
          </form>
        </Form>
      </SettingsCard>
    </SettingsSection>
  );
}
